import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CallNotice, PhoneCallService } from '../../services/phone-call.service';

/** One supplier row, as returned by /rfq/getRfqSupplierContacts. */
export interface RfqSupplierContact {
  supplierId: number;
  companyName: string;
  contactPerson: string | null;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  alternatePhone: string | null;
  fax: string | null;
  vendorCode: string | null;
  website: string | null;
  hasQuoted: boolean;
  quoteRefNumber: string | null;
  viewed: boolean;
  callable: boolean;
}

/**
 * Every supplier an RFQ went to, with the contact details needed to ring them.
 *
 * The buyer list has always shown a supplier COUNT. A count answers "how many"
 * and nothing else — the buyer chasing a quote still had to go and look each
 * company up. This turns the count into the list it stands for, and puts the
 * call one click away.
 */
@Component({
  selector: 'app-supplier-contacts',
  templateUrl: './supplier-contacts.component.html',
  styleUrls: ['./supplier-contacts.component.css'],
})
export class SupplierContactsComponent {
  @Input() open = false;
  @Input() loading = false;
  @Input() rfqRefNumber: string | null = null;
  @Input() suppliers: RfqSupplierContact[] = [];

  /** Set when the lookup failed, so the panel can say why. */
  @Input() error = '';

  @Output() close = new EventEmitter<void>();

  /** The call result currently on screen. One at a time, by design. */
  notice: CallNotice | null = null;

  /** Which row was last dialled, so it can be highlighted. */
  activeSupplierId: number | null = null;

  search = '';

  constructor(private phoneCall: PhoneCallService) {}

  get provider(): string {
    return this.phoneCall.provider;
  }

  get callingEnabled(): boolean {
    return this.phoneCall.enabled;
  }

  get callableCount(): number {
    return (this.suppliers || []).filter((s) => s.callable).length;
  }

  /** Suppliers matching the filter box; the whole list when it is empty. */
  get visibleSuppliers(): RfqSupplierContact[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.suppliers || [];
    return (this.suppliers || []).filter((s) =>
      [s.companyName, s.contactPerson, s.email, s.phone, s.vendorCode]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }

  initials(supplier: RfqSupplierContact): string {
    const source = (supplier?.companyName || supplier?.contactPerson || '').trim();
    if (!source) return '?';
    const words = source.split(/\s+/).filter(Boolean);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }

  /** Rings the supplier and shows what happened. */
  callSupplier(supplier: RfqSupplierContact): void {
    this.activeSupplierId = supplier.supplierId;
    this.notice = this.phoneCall.call(
      supplier.phone || supplier.alternatePhone,
      supplier.companyName
    );
  }

  dismissNotice(): void {
    this.notice = null;
    this.activeSupplierId = null;
  }

  closePanel(): void {
    this.dismissNotice();
    this.search = '';
    this.close.emit();
  }

  trackBySupplier(_index: number, supplier: RfqSupplierContact): number {
    return supplier.supplierId;
  }
}
